import { TILE, type GeneratedMap } from './MapGen';

/**
 * Tile queries + circle-vs-wall movement resolution (axis-separated slide).
 * World coordinates: tile t spans [t*TILE, (t+1)*TILE); centers at (t+0.5)*TILE.
 */
export class TileMap {
  readonly w: number;
  readonly h: number;
  private readonly tiles: Uint8Array;

  constructor(map: GeneratedMap) {
    this.w = map.w;
    this.h = map.h;
    this.tiles = map.tiles;
  }

  isWallTile(tx: number, ty: number): boolean {
    if (tx < 0 || tx >= this.w || ty < 0 || ty >= this.h) return true;
    return this.tiles[ty * this.w + tx] === 0;
  }

  /** Point test in world coords — bullets use this (die on wall contact). */
  isWallAt(x: number, y: number): boolean {
    return this.isWallTile(Math.floor(x / TILE), Math.floor(y / TILE));
  }

  /** Circle-vs-walls overlap test in world coords. */
  circleCollides(x: number, y: number, r: number): boolean {
    const minTx = Math.floor((x - r) / TILE);
    const maxTx = Math.floor((x + r) / TILE);
    const minTy = Math.floor((y - r) / TILE);
    const maxTy = Math.floor((y + r) / TILE);
    for (let ty = minTy; ty <= maxTy; ty++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        if (!this.isWallTile(tx, ty)) continue;
        // Closest point on the tile rect to the circle center.
        const cx = Math.max(tx * TILE, Math.min(x, (tx + 1) * TILE));
        const cy = Math.max(ty * TILE, Math.min(y, (ty + 1) * TILE));
        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy < r * r) return true;
      }
    }
    return false;
  }

  /** Sampled line-of-sight test (24px steps) — true if a wall interrupts. */
  lineBlocked(x0: number, y0: number, x1: number, y1: number): boolean {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const steps = Math.ceil(Math.hypot(dx, dy) / 24);
    for (let i = 1; i < steps; i++) {
      if (this.isWallAt(x0 + (dx * i) / steps, y0 + (dy * i) / steps)) return true;
    }
    return false;
  }

  /** Axis-separated move: slide along walls instead of sticking. */
  resolveMove(px: number, py: number, nx: number, ny: number, r: number): [number, number] {
    let x = px;
    let y = py;
    if (!this.circleCollides(nx, y, r)) x = nx;
    if (!this.circleCollides(x, ny, r)) y = ny;
    return [x, y];
  }

  static toWorld(t: number): number {
    return (t + 0.5) * TILE;
  }
}
